import { fmtDay } from '@/lib/dates';
import { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShieldCheck, CalendarClock } from 'lucide-react';
import { StartDateChooser, todayYmd } from '@/components/ProgrammeStartDate';

const fmt = (ymd) => fmtDay(ymd, { day: 'numeric', month: 'short', year: 'numeric' });

// Coach view: subscription paid-through date + Protocol programme awaiting approval.
export function ProgrammeApproval({ client, onChanged }) {
  const [busy, setBusy] = useState(false);
  const sub = client.subscription;
  const pb = client.pending_block;
  const [start, setStart] = useState(() => (pb?.start_date && pb.start_date > todayYmd() ? pb.start_date : todayYmd()));
  if (!sub && !pb) return null;
  const release = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/clients/${client.id}/release-programme`, { start_date: start });
      toast.success(r.data.released_now ? 'Client activated — programme released' : `Approved — it will start on ${fmt(r.data.start_date)}`);
      await onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not release'); }
    finally { setBusy(false); }
  };
  const saveDate = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/clients/${client.id}/programme-start`, { start_date: start });
      toast.success(r.data.pending ? `Start date set to ${fmt(start)}` : 'Programme released — it starts today');
      await onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change the start date'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      {sub && (
        <div className="regen-card p-4 flex items-center gap-3 text-sm" data-testid="cd-subscription">
          <CalendarClock className="w-4 h-4 text-[#00C8FF]" />
          <span>{sub.plan === 'regen_plus' ? 'YourReGen+' : 'YourReGen Protocol'} · {sub.term_months} month{sub.term_months > 1 ? 's' : ''} ·{' '}
            {sub.status === 'active' ? `paid through ${fmt(sub.paid_through_date)} · next period ${fmt(sub.next_start)}` : `ended ${fmt(sub.paid_through_date)}`}
            {sub.auto_renew ? ' · auto-renews monthly' : ''}</span>
        </div>
      )}
      {pb && (
        <div className="rounded-2xl border border-[#FFB800]/40 bg-[#FFB800]/10 p-4 space-y-3" data-testid="cd-programme-approval">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-[#FFB800] shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold">{pb.approved ? 'Programme approved' : 'Programme awaiting your review'}</div>
            <div className="text-xs text-muted-foreground" data-testid="cd-pending-start">Training + meal plan ({pb.goal}) starting {fmt(pb.start_date)}.
              {pb.approved ? ' It will be released automatically on its start date.' : ' Review every session in the Training tab below, make any corrections, choose the start date, then activate. The client sees nothing until you do.'}</div>
          </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="label-mini mr-1">Programme starts</span>
            <StartDateChooser value={start} onChange={setStart} testid="cd-approve-start" />
            {pb.approved
              ? <Button disabled={busy || start === pb.start_date} onClick={saveDate} data-testid="cd-pending-start-save" className="bg-[#00C8FF] text-[#081726] font-semibold">Change start date</Button>
              : <Button disabled={busy || start < todayYmd()} onClick={release} data-testid="cd-release-programme" className="bg-[#FFB800] text-[#081726] font-semibold">Approve & activate</Button>}
          </div>
          <p className="text-[11px] text-muted-foreground">Nutrition and training both begin on this date (Week 1, Day 1). Days before it are not counted. If it is later than today, paid access is extended by the same number of days.</p>
        </div>
      )}
    </div>
  );
}
